import type { StorageApi } from '@backstage/core-plugin-api';

/**
 * Palette themes — a color scheme that re-points the foundation `--c-*` tokens
 * (see the [data-palette] blocks in tailwind.css), orthogonal to the
 * light/dark mode. Persisted in localStorage so the choice survives re-login
 * (per browser); applied by toggling `data-palette` on <html>.
 */
export interface PaletteOption {
  id: string;
  label: string;
}

/** Sentinel id for the built-in Untitled UI scheme (no `data-palette` attr). */
export const DEFAULT_PALETTE = 'default';

export const PALETTES: PaletteOption[] = [
  { id: DEFAULT_PALETTE, label: 'Default Theme' },
  { id: 'catppuccin', label: 'Catppuccin' },
  { id: 'hue', label: 'Huế' },
  { id: 'solarized', label: 'Solarized' },
  { id: 'gruvbox', label: 'Gruvbox' },
  { id: 'nord', label: 'Nord' },
  { id: 'everforest', label: 'Everforest' },
  { id: 'kanagawa', label: 'Kanagawa' },
];

const STORAGE_KEY = 'regask-palette';
export const PALETTE_CHANGE_EVENT = 'regask-palette-change';

/**
 * Shared StorageApi bucket + keys for per-user appearance prefs, persisted
 * server-side via the user-settings backend (falls back to localStorage when
 * signed out or the backend is unavailable). The theme mode lives alongside the
 * palette here; see RegaskAppThemeApi in ./index.tsx.
 */
export const APPEARANCE_BUCKET = 'appearance';
export const PALETTE_BACKEND_KEY = 'palette';
export const THEME_MODE_KEY = 'themeMode';
/** Theme mode preference. `system` follows the OS `prefers-color-scheme`. */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * localStorage mirror of the theme mode, read synchronously pre-paint to avoid
 * a flash before the (async) backend value arrives — the StorageApi `snapshot`
 * is always `'unknown'`, so it can't seed the initial theme on its own.
 */
const THEME_MODE_STORAGE = 'regask-theme-mode';

/** Synchronous theme mode from the localStorage mirror (null if unset). */
export function getStoredThemeMode(): ThemeMode | null {
  try {
    const value = localStorage.getItem(THEME_MODE_STORAGE);
    return value === 'light' || value === 'dark' || value === 'system'
      ? value
      : null;
  } catch {
    return null;
  }
}

/** Update the localStorage theme mode mirror. */
export function setStoredThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_MODE_STORAGE, mode);
  } catch {
    // Private mode / storage disabled — fall through to session-only.
  }
}

/** The persisted palette id (`'default'` = built-in Untitled UI). */
export function getStoredPalette(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_PALETTE;
  } catch {
    return DEFAULT_PALETTE;
  }
}

/** Set/clear `data-palette` on the document root. */
export function applyPalette(id: string): void {
  const root = document.documentElement;
  if (id && id !== DEFAULT_PALETTE) root.setAttribute('data-palette', id);
  else root.removeAttribute('data-palette');
}

/** Persist + apply the palette and notify subscribers. */
export function setStoredPalette(id: string): void {
  try {
    if (id && id !== DEFAULT_PALETTE) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — fall through to applying for the session.
  }
  applyPalette(id);
  window.dispatchEvent(new CustomEvent(PALETTE_CHANGE_EVENT, { detail: id }));
}

/** Subscribe to palette changes; returns an unsubscribe fn. */
export function subscribePalette(callback: (id: string) => void): () => void {
  const handler = (event: Event) =>
    callback((event as CustomEvent<string>).detail ?? '');
  window.addEventListener(PALETTE_CHANGE_EVENT, handler);
  return () => window.removeEventListener(PALETTE_CHANGE_EVENT, handler);
}

/** The `appearance` bucket on the (backend-backed) StorageApi. */
function appearanceBucket(storage: StorageApi): StorageApi {
  return storage.forBucket(APPEARANCE_BUCKET);
}

/**
 * Persist the palette choice server-side (per user). The `default` palette is
 * stored explicitly (rather than cleared) so that resetting to it propagates to
 * other devices, and so an absent backend value can be treated as "never synced"
 * — see {@link subscribePaletteBackend}.
 */
export function persistPaletteToBackend(storage: StorageApi, id: string): void {
  void appearanceBucket(storage).set(PALETTE_BACKEND_KEY, id || DEFAULT_PALETTE);
}

/**
 * Apply the user's server-stored palette on load and on cross-device/tab
 * changes, keeping the localStorage mirror + subscribers in sync. Returns an
 * unsubscribe fn.
 *
 * Only acts on a *present* backend value: an absent value means this user has
 * never synced a palette, so we leave their existing (possibly localStorage-only)
 * choice untouched rather than resetting it to the default.
 */
export function subscribePaletteBackend(storage: StorageApi): () => void {
  const sub = appearanceBucket(storage)
    .observe$<string>(PALETTE_BACKEND_KEY)
    .subscribe(snapshot => {
      if (snapshot.presence !== 'present') return;
      if (snapshot.value !== getStoredPalette()) setStoredPalette(snapshot.value);
    });
  return () => sub.unsubscribe();
}
